import { createLiteYouTubeEmbed } from "./LiteYouTubeEmbed.js";

/**
 * VideoController - Dynamic keyword-based video system
 * Now uses GPT-4.1 Nano for keyword extraction + YouTube API for video search
 * Handles video display in chat interface with minimal, clean design
 */
export class VideoController {
    constructor() {
        this.videoContainer = null;
        this.currentVideos = [];
        this.isInitialized = false;

        // Characters with video system support (all characters now supported via dynamic system)
        this.supportedCharacters = [
            "bc5d31a8-047b-4f72-8f16-fea7a365ca69", // Richard Feynman
            "88c314c4-5890-4e55-9418-8b46a376ef81a", // Friedrich Nietzsche
            "4f50a698-6034-49c1-b9d4-221f59bf2378", // Frida Kahlo
            "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9", // Nostradamus
            "01db85dc-81e0-4433-9440-a54ce4976178", // Erwin Schrodinger
            "2db1d398-0161-46cd-8aff-53008d29a9dc", // Claude Shannon
            "c2cc71ce-89e7-4c94-9441-eec86ec67a60", // Napoleon Bonaparte
            "0c6099ed-c0db-4dd7-bc46-1d720324db5a", // Michael Faraday
            "1a35136c-42d8-46c7-a189-c4b32da87832", // John Neumann
            "be0dcf0f-c72b-48f7-a462-6ba7560498d7", // Alan Turing
            "d537d61d-0313-421b-ae66-48a8d7222b3f", // Nikola Tesla
            "16f5b6a1-45f6-4c01-a612-60d4e4ab2297", // René Descartes
            "5eb1ad8d-4c11-4174-b79a-c232991d015b", // Oppenheimer
            "7021ffd6-51bb-41a8-920e-935899dcd631", // Isaac Newton
            "93ea7051-9722-443d-90fb-18a84c51894c", // James Clerk Maxwell
            "b20dc26f-8924-4aee-9ef5-9b98c0367a34", // Ernest Hemingway
            "897eb00c-4f16-4f73-81d5-d1c91fad53f2", // Charles Darwin
            "73f610bf-d9c9-4d9f-a026-a10086a66725", // Marie Curie
            "802bd089-d231-4c4e-830c-476199321af8", // Niels Bohr
            "992e9007-b4f8-4711-80c2-884daf3d9960", // Van Gogh
            "770e2d93-a6a4-41ed-aca1-8951327885b5", // Amelia Earhart
            "5a16ff56-6e03-414e-a89d-cda9d83fae60", // Henry Ford
            "031f2eea-64e5-441d-a37d-03d260cc43dc", // Abraham Lincoln
            "a59fe2c1-9046-405a-9a99-a7ddbaee13be", // Adam Smith
            "e3f55c33-3ff0-471f-b833-b59d7b145fc6", // Al Capone
            "c4b9ac58-a66a-451e-b035-afabd8be50b9", // Aristotle
            "b8951237-698b-42b5-8b6e-ee381b91fb65", // Confucius
            "9ef81498-3fb9-4175-a6d4-c2a02d71c640", // Cyrus The Great
            "d82bc58b-5487-4af2-ad5e-fa9fa838e239", // Charles Darwin
            "8944783c-2e2b-4796-b92e-21dacca776bc", // Leonardo da Vinci
            "d6e3a3f1-d31e-4aab-8dd0-8b820932bd13", // Enrico Fermi
            "8f109c00-f7d2-4b9b-b44e-740a55e4339e", // George Washington
            "4d7d2e17-b818-4144-b78b-629c688a71ac", // Karl Marx
            "2baa0479-7e38-4473-a2f2-1ad54149dbac", // Muhammad Ali
            "9039a447-b6eb-4de2-9927-0b0c6476c090", // Malcolm X
            "3f14429d-b2d4-4b99-8a05-58eed567c0d3", // Mansa Musa
            "e97568c5-1daa-4a95-b4ae-0fe374c6b083", // Marcus Aurelius
            "d0c97034-328e-4538-8107-a9825d0c1594", // Martin Luther King
            "7716e2f6-eb48-424d-a962-252b15ca634e", // Newton
            "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b", // Niccolo Machiavelli
            "8ded3849-e5ad-4dea-9bf5-3d8f912e29cf", // Robert Oppenheimer
            "59c67a42-8c1e-45b8-aa41-b6fe6b3f5629", // George Orwell
            "3542f19e-4e07-4828-8f9d-f330e4d6d660", // Plato
            "d75e63e0-2b2b-43ef-933f-589e8d6818c0", // Salvador Dali
            "548fe879-e63c-4d6c-a377-525f4f9e12c8", // Satoshi Nakamoto
            "d9f9af52-ae93-4bd2-abd4-0b7032844ddd", // Nikola Tesla
            "7c7ac397-c36e-4218-92d4-f437d1959cca", // Euclid
            "f9f3f836-00c6-4160-8416-4b71b53f1307", // Srinivasa Ramanujan
            "5802e813-e715-491d-a0fd-92dc643c458e", // Blaise Pascal
            "191e6103-a42c-4132-926c-230e7b1be5b6", // Jacob Bernoulli
            "6bcf3678-e232-46c9-996a-67beb3fd2dbf", // Al-Khwarizmi
            "14836d13-ace6-4bf8-be26-26fbb80ed9bc", // Socrates
            "3841a6bb-f977-453d-92f5-6cec1267546f", // Sigmund Freud
            "c0063a05-916b-45d0-bc2c-c63210414b17", // Ada Lovelace
            "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff", // Carl Friedrich Gauss
            "127cd993-f16e-4659-84fa-882cba633097", // Galileo Galilei
            "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f", // Grace Hopper
            "c93478a1-ed2f-4f1a-81f6-3fa474a21566", // Joseph-Louis Lagrange
            "5a39bed2-a551-4a03-98a2-46104a3324bf", // Leonhard Euler
            "2707ca40-df64-4258-97c8-67d3695e8a42", // Brahmagupta
            "e617290b-bda0-4771-91ac-3fe6939dd412", // Benjamin Franklin
            "a2a43496-9a83-4444-813e-18a1930296e6", // Omar Khayyam
            "78376372-3d71-4fce-8650-82b5be2c006b", // Diophantus
            "ca6ebdca-5151-4a58-8523-56d25fce5dfa", // William Playfair
            "e3428624-bd1e-48b9-ae8c-c66ba68879cc", // George Boole
            "3140d634-c7ca-4bda-af36-e1b49978b201", // Thomas Bayes
            "c73fed54-81e3-46ec-9d7c-3d11d05b142d", // Eratosthenes
            "ece0ab37-37c1-4ead-8222-fbb5b185dbe2", // Alessandro Volta
            "d13b5a81-ed77-4a25-8a01-a76a0165b349", // Archimedes
            "8f67d579-41c2-456d-900b-fccbcbf46bd3", // François Viète
            "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc", // Felix Klein
            "44f87c0b-ca22-4225-9fea-8d823e6a0160", // Warren Buffett
            "581e7293-43cf-45ea-9fb3-0955c563cbe2", // Genghis Khan
            "278dddd6-b4a8-4303-a41d-be6219dbc907", // Albert Einstein
        ];

        this.initialize();
    }

    /**
     * Initialize the VideoController
     */
    initialize() {
        console.log("🎥 VideoController: Starting initialization");

        // Wait for DOM to be ready
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () =>
                this.setupVideoContainer(),
            );
        } else {
            this.setupVideoContainer();
        }
    }

    /**
     * Set up the video container reference
     */
    setupVideoContainer() {
        this.videoContainer = document.getElementById("video-display");

        if (this.videoContainer) {
            console.log("🎥 VideoController: Container found and ready");
            this.clearContainer();
            this.isInitialized = true;
        } else {
            console.error(
                "🎥 VideoController: video-display container not found",
            );
            this.isInitialized = false;
        }
    }

    /**
     * Clear the video container completely
     */
    clearContainer() {
        if (this.videoContainer) {
            this.videoContainer.innerHTML = "";
            this.videoContainer.style.display = "block";
            console.log("🎥 VideoController: Container cleared");
        }
    }

    /**
     * Check if current character supports video system
     */
    isVideoSupportedCharacter() {
        try {
            // Get current model from global scope
            if (window.modelViewer && window.modelViewer.currentModelKey) {
                const currentModel = window.modelViewer.currentModelKey;
                console.log(
                    `🎥 VideoController: Checking video support for model: ${currentModel}`,
                );
                return this.supportedCharacters.includes(currentModel);
            }
            return false;
        } catch (error) {
            console.error(
                "🎥 VideoController: Error checking video support:",
                error,
            );
            return false;
        }
    }

    /**
     * Process video data from chat response
     * Now handles dynamic YouTube search results
     */
    processVideoData(videoData, messageId = null) {
        console.log("🎥 VideoController: Processing video data:", videoData);

        if (!this.isInitialized) {
            console.warn("🎥 VideoController: Not initialized, skipping");
            return;
        }

        if (!this.isVideoSupportedCharacter()) {
            console.log(
                "🎥 VideoController: Current character does not support videos",
            );
            return;
        }

        if (
            !videoData ||
            !videoData.has_videos ||
            !videoData.videos ||
            videoData.videos.length === 0
        ) {
            console.log("🎥 VideoController: No videos to display");
            this.showEmptyState();
            return;
        }

        // Log source and extracted keywords for dynamic video system
        if (videoData.source === "dynamic_youtube_search") {
            console.log("🎥 VideoController: Using dynamic YouTube search");
            if (videoData.extracted_keywords) {
                console.log(
                    `🎥 VideoController: Extracted keywords: ${videoData.extracted_keywords.join(", ")}`,
                );
            }
            if (videoData.character_name) {
                console.log(
                    `🎥 VideoController: Character: ${videoData.character_name}`,
                );
            }
        }

        console.log(
            `🎥 VideoController: Displaying ${videoData.videos.length} videos`,
        );
        this.displayVideos(videoData.videos);
    }

    /**
     * Display videos in clean, minimal layout
     */
    displayVideos(videos) {
        if (!this.videoContainer || !videos || videos.length === 0) {
            this.showEmptyState();
            return;
        }

        console.log(`🎥 VideoController: Rendering ${videos.length} videos`);

        // Clear container completely
        this.clearContainer();

        // Create main container
        const videosWrapper = document.createElement("div");
        videosWrapper.className = "videos-wrapper";
        videosWrapper.style.cssText = `
            width: 100%;
            height: 100%;
            overflow-y: auto;
            overflow-x: hidden;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 20px;
            scrollbar-width: none;
            -ms-overflow-style: none;
        `;

        // Hide scrollbar for webkit browsers
        const styleSheet = document.createElement("style");
        styleSheet.textContent = `
            .videos-wrapper::-webkit-scrollbar {
                display: none;
            }
            .video-item-container {
                position: relative;
                width: 100%;
                padding-bottom: 56.25%; /* 16:9 aspect ratio */
                height: 0;
                background: #000;
                border-radius: 8px;
                overflow: hidden;
            }
            .video-item-container iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                border: none;
            }
            .video-keyword-tag {
                position: absolute;
                top: 8px;
                right: 8px;
                background: rgba(184, 149, 45, 0.3);
                color: white;
                padding: 6px 12px;
                border-radius: 10px;
                font-size: 11px;
                font-weight: 500;
                text-transform: capitalize;
                z-index: 10;
                pointer-events: none;
            }
        `;
        document.head.appendChild(styleSheet);

        // Create each video item with keyword header
        videos.forEach((video, index) => {
            const videoItem = this.createVideoItem(video, index);
            videosWrapper.appendChild(videoItem);
        });

        this.videoContainer.appendChild(videosWrapper);
        this.currentVideos = videos;

        console.log(
            `🎥 VideoController: Successfully rendered ${videos.length} videos`,
        );
    }

    /**
     * Create individual video item with keyword header
     * Handles both embedded videos and search link fallbacks
     */
    createVideoItem(video, index) {
        // Create wrapper for keyword + video
        const wrapper = document.createElement("div");
        wrapper.className = "video-item-wrapper";

        // Create keyword header (matching image keyword style)
        const keywordHeader = document.createElement("div");
        keywordHeader.className = "video-keyword-header";

        // Show video title if available, otherwise use keyword
        const displayTitle =
            video.title || video.keyword || `Video ${index + 1}`;
        keywordHeader.textContent = video.keyword || displayTitle;

        // Create video container
        const container = document.createElement("div");
        container.className = "video-item-container";

        // Check if this is a search link fallback (no API key available)
        if (video.is_search_link && video.youtube_url) {
            // Create a search link card instead of embedded video
            container.innerHTML = `
                <a href="${video.youtube_url}" target="_blank" rel="noopener noreferrer" 
                   style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; 
                          display: flex; flex-direction: column; align-items: center; 
                          justify-content: center; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
                          text-decoration: none; color: white;">
                    <i class="fab fa-youtube" style="font-size: 48px; color: #ff0000; margin-bottom: 12px;"></i>
                    <span style="font-size: 14px; text-align: center; padding: 0 20px;">${video.title || "Search on YouTube"}</span>
                    <span style="font-size: 11px; opacity: 0.7; margin-top: 8px;">Click to open YouTube</span>
                </a>
            `;
        } else if (video.embed_html) {
            // Task #274: click-to-load facade instead of an eager YouTube
            // iframe (memory pressure on iPhone Safari — see
            // LiteYouTubeEmbed.js). Falls back to the legacy iframe when
            // no video id can be resolved.
            const facade = createLiteYouTubeEmbed(video);
            if (facade) {
                container.appendChild(facade);
            } else {
                // Standard embedded video from YouTube API or static config
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = video.embed_html;
                const iframe = tempDiv.querySelector("iframe");

                if (iframe) {
                    // Remove existing width/height attributes
                    iframe.removeAttribute("width");
                    iframe.removeAttribute("height");
                    iframe.style.cssText =
                        "position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;";

                    container.appendChild(iframe);
                } else {
                    console.error(
                        `🎥 VideoController: No iframe found in embed HTML for video ${index}`,
                    );
                    // Fallback to YouTube URL if available
                    if (video.youtube_url) {
                        container.innerHTML = `
                            <a href="${video.youtube_url}" target="_blank" rel="noopener noreferrer"
                               style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; 
                                      display: flex; flex-direction: column; align-items: center; 
                                      justify-content: center; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
                                      text-decoration: none; color: white;">
                                <i class="fab fa-youtube" style="font-size: 48px; color: #ff0000; margin-bottom: 12px;"></i>
                                <span style="font-size: 14px;">Watch on YouTube</span>
                            </a>
                        `;
                    }
                }
            }
        } else if (video.youtube_url) {
            // No embed HTML but have YouTube URL - create link
            container.innerHTML = `
                <a href="${video.youtube_url}" target="_blank" rel="noopener noreferrer"
                   style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; 
                          display: flex; flex-direction: column; align-items: center; 
                          justify-content: center; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
                          text-decoration: none; color: white;">
                    <i class="fab fa-youtube" style="font-size: 48px; color: #ff0000; margin-bottom: 12px;"></i>
                    <span style="font-size: 14px; text-align: center; padding: 0 20px;">${video.title || "Watch on YouTube"}</span>
                </a>
            `;
        }

        wrapper.appendChild(keywordHeader);
        wrapper.appendChild(container);

        // Add video info if available (channel, description)
        if (video.channel || video.description) {
            const infoDiv = document.createElement("div");
            infoDiv.className = "video-info";
            infoDiv.style.cssText = `
                padding: 8px 12px;
                background: rgba(0, 0, 0, 0.3);
                border-radius: 0 0 8px 8px;
                font-size: 12px;
                color: rgba(255, 255, 255, 0.8);
            `;

            if (video.channel) {
                const channelSpan = document.createElement("span");
                channelSpan.style.cssText =
                    "display: block; font-weight: 500; margin-bottom: 4px;";
                channelSpan.textContent = video.channel;
                infoDiv.appendChild(channelSpan);
            }

            if (video.description && video.description.length > 0) {
                const descSpan = document.createElement("span");
                descSpan.style.cssText =
                    "display: block; opacity: 0.7; font-size: 11px;";
                descSpan.textContent =
                    video.description.substring(0, 100) +
                    (video.description.length > 100 ? "..." : "");
                infoDiv.appendChild(descSpan);
            }

            wrapper.appendChild(infoDiv);
        }

        return wrapper;
    }

    /**
     * Show empty state when no videos available
     */
    showEmptyState() {
        if (!this.videoContainer) return;

        this.clearContainer();

        const emptyState = document.createElement("div");
        emptyState.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: rgba(255, 255, 255, 0.6);
            text-align: center;
            padding: 40px 20px;
        `;

        emptyState.innerHTML = `
            <i class="fas fa-video" style="font-size: 48px; margin-bottom: 16px; opacity: 0.4;"></i>
            <p style="margin: 0; font-size: 16px;">No videos available</p>
            <small style="margin-top: 8px; opacity: 0.7;">Ask about topics like quantum physics or science</small>
        `;

        this.videoContainer.appendChild(emptyState);
    }

    /**
     * Clear all videos and show empty state
     */
    clearVideos() {
        this.currentVideos = [];
        this.showEmptyState();
    }

    /**
     * Get current videos count
     */
    getVideoCount() {
        return this.currentVideos.length;
    }
}

// Export for global access
window.VideoController = VideoController;
